import { z } from "zod";

/**
 * Contracts of the manager incident response inbox and the case summary (incidents
 * redesign, GitHub #497, Phase 05, Task 3 server half). Pure types and schemas: no DB,
 * Express or runtime-environment imports, so the API, the actions and the web hooks
 * share one shape. Every value shown here is derived on the server; the client never
 * computes readiness, the next action or a capability itself.
 */

export const INCIDENT_INBOX_FILTERS = [
  "needs_action",
  "new",
  "assigned_to_me",
  "awaiting_evidence",
  "reporting_due",
  "ready_for_signoff",
  "all",
] as const;
export type IncidentInboxFilter = (typeof INCIDENT_INBOX_FILTERS)[number];

export const INCIDENT_INBOX_SORTS = [
  "soonest_action",
  "newest",
  "severity",
] as const;
export type IncidentInboxSort = (typeof INCIDENT_INBOX_SORTS)[number];

export const INCIDENT_INBOX_MAX_PAGE_SIZE = 100;
export const INCIDENT_INBOX_DEFAULT_PAGE_SIZE = 20;

/** Query string of GET inbox: every key optional, defaults applied by the action. */
export const incidentInboxQuerySchema = z.object({
  filter: z.enum(INCIDENT_INBOX_FILTERS).default("needs_action"),
  sort: z.enum(INCIDENT_INBOX_SORTS).default("soonest_action"),
  search: z.string().trim().min(1).max(200).optional(),
  facilityId: z.string().uuid().optional(),
  ownerMemberId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(INCIDENT_INBOX_MAX_PAGE_SIZE)
    .default(INCIDENT_INBOX_DEFAULT_PAGE_SIZE),
});
export type IncidentInboxQuery = z.infer<typeof incidentInboxQuerySchema>;

/**
 * Where an overdue or upcoming time comes from. An internal response clock and an
 * external regulator deadline are different obligations and are never merged:
 * `internal_sla` is the organisation's own response target, `external_duty` is a
 * deadline set by a regulator (the persisted reporting duty's due time), `task` is a
 * case task or form due date and `review` carries no deadline of its own.
 */
export type IncidentNextActionKind =
  | "internal_sla"
  | "external_duty"
  | "task"
  | "review";

export type IncidentNextActionCode =
  | "acknowledge_and_assign"
  | "assign_investigator"
  | "assess_reporting"
  | "submit_report"
  | "review_evidence"
  | "complete_forms"
  | "complete_tasks"
  | "review_investigation"
  | "continue_investigation"
  | "none";

export type IncidentCaseTab =
  | "overview"
  | "forms"
  | "tasks"
  | "timeline"
  | "ndis"
  | "evidence"
  | "signoff";

export interface IncidentNextAction {
  code: IncidentNextActionCode;
  label: string;
  kind: IncidentNextActionKind;
  /** ISO instant, or null when the action has no deadline. */
  dueAt: string | null;
  /** What the due time is: the internal response target, the regulator's deadline or a task date. */
  dueBasis: "response_sla" | "regulator_deadline" | "task_due" | null;
  /** Plain words for the due time, for example "Response due" or "Commission report due". */
  dueLabel: string | null;
  /** The case tab that does this work. */
  target: IncidentCaseTab;
}

export type IncidentStatusTone =
  | "neutral"
  | "info"
  | "warning"
  | "danger"
  | "success"
  | "review";

export interface IncidentSeverityDisplay {
  level: "low" | "medium" | "high" | "critical" | null;
  /** `confirmed` is the human-confirmed severity; `reported` is what the reporter chose. */
  provenance: "confirmed" | "reported" | null;
  label: string;
}

export interface IncidentInboxRow {
  id: string;
  incidentNumber: string | null;
  title: string | null;
  /** First name and last initial; null when the incident has no participant. */
  participantLabel: string | null;
  placeLabel: string;
  occurredAtLabelSource: {
    occurredAt: string | null;
    reportedAt: string;
    occurrenceState: string | null;
  };
  status: string;
  statusLabel: string;
  statusTone: IncidentStatusTone;
  /** "Legacy" for a row that predates the redesigned workflow; otherwise null. */
  provenanceLabel: "Legacy" | null;
  nextAction: IncidentNextAction;
  ownerMemberId: string | null;
  ownerLabel: string | null;
  severity: IncidentSeverityDisplay;
}

export interface IncidentInboxCounts {
  /** Open rows whose next action is anything other than continuing the investigation. */
  needsActionToday: number;
  unacknowledged: number;
  readyForSignoff: number;
  /** One number per filter, over the same scope, search and facets as `rows`. */
  byFilter: Record<IncidentInboxFilter, number>;
}

export interface IncidentInboxResult {
  counts: IncidentInboxCounts;
  rows: IncidentInboxRow[];
  page: number;
  pageSize: number;
  /** Rows matching the selected filter (equals `counts.byFilter[filter]`). */
  total: number;
  /** What this person may do on the inbox page itself, decided on the server. */
  capabilities: IncidentInboxCapabilities;
  /** The latest published report in this person's own scope, or null when there is none. */
  recentHandoff: IncidentInboxHandoff | null;
}

/** Page-level capabilities of the inbox (case-level ones live on the case summary). */
export interface IncidentInboxCapabilities {
  /** Whether the urgent-alert list may be read by this person (the same rule the read applies). */
  canReadUrgentAlerts: boolean;
}

/**
 * The "Recent handoff" card: the latest published report, independent of the filter and
 * facets. Facts only: no narrative, no participant detail.
 */
export interface IncidentInboxHandoff {
  incidentId: string;
  incidentNumber: string | null;
  /** First name and last initial of the reporter. */
  reporterLabel: string | null;
  /** ISO instant the report was submitted. */
  submittedAt: string;
  /** The reporter's original narrative is held on the record. */
  narrativeRetained: boolean;
  /** Photos that finished uploading. */
  photoCount: number;
  /** At least one recorded injury has no assessed outcome yet. */
  injuryOutcomeUnknown: boolean;
}

export interface IncidentCaseCapabilities {
  canInvestigate: boolean;
  canReview: boolean;
  canExport: boolean;
  canShareEvidence: boolean;
  canSubmitRegulatory: boolean;
  /** Owners and acknowledgement: the case team of an open incident. */
  canAssignOwners: boolean;
  canAcknowledge: boolean;
  canConfirmSeverity: boolean;
  /** False once the incident is closed; every mutating control is off. */
  canMutate: boolean;
}

export interface IncidentCaseSummary {
  incidentId: string;
  incidentNumber: string | null;
  title: string | null;
  status: string;
  statusLabel: string;
  statusTone: IncidentStatusTone;
  version: number;
  provenanceLabel: "Legacy" | null;
  investigator: { memberId: string; label: string } | null;
  approver: { memberId: string; label: string } | null;
  /** "High · manager assessed" or "Medium · reported". */
  priority: {
    level: IncidentSeverityDisplay["level"];
    source: "manager_assessed" | "reported" | null;
    label: string;
  };
  response: {
    acknowledgedAt: string | null;
    acknowledgedByLabel: string | null;
    /** The internal response target, never a regulator deadline. */
    dueAt: string | null;
    label: string;
  };
  reportability: {
    state: "decision_needed" | "reportable" | "not_reportable";
    label: string;
  };
  nextAction: IncidentNextAction;
  capabilities: IncidentCaseCapabilities;
  /**
   * True unless the newest decision of the case team shows the incident in the
   * participant and family view; true when nobody decided (the default). The
   * server always sets it; it is optional only so a summary built without it still types.
   */
  participantViewWithheld?: boolean;
}
